import { memo, useCallback, useEffect, useState } from "react";
import { Tabs, Tab, Typography, Box, Button } from "@mui/material";
import CustomerTimeline from "./CustomerTimeline";
import Insights from "./Insights";
import Details from "./Details";
import WhatsAppDrawer from "../WhatsAppDrawer/WhatsApp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { setModalOpen } from "@/store/slices/globalSlice";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import { useAppDispatch } from "@/store/hooks";
import { MODAL_ID } from "@/lib/constants";
import AISummaryDialog from "./AISummaryDialog";
import { usePermissions } from "@/hooks/usePermissions";
// import WhatsAppDrawer from "../WhatsAppDrawer/WhatsApp";

interface TimelineDrawerProps {
  statusList: any[];
  visitPurposeList: any[];
  leadId: number;
  leadName: string;
  opportunityId?: number;
  opportunityData?: any;
  oppNumber: number;
  type: string;
  phoneNumber?: any;
  isCustomer?: boolean;
  /** Hide top header (customer name + extra spacing). */
  hideHeader?: boolean;
  /** Mobile route: show Insights only (no Timeline / Details / WhatsApp tabs). */
  insightsOnly?: boolean;
  outerActiveTab?:number;
}

const TimelineDrawer: React.FC<TimelineDrawerProps> = ({ statusList, visitPurposeList, leadId, leadName, opportunityId, opportunityData, oppNumber, type, phoneNumber, isCustomer, hideHeader, insightsOnly, outerActiveTab = 0 }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { modalOpen } = useSelector((state: RootState) => state.global);
  const dispatch = useAppDispatch();
  const [summaryText, setSummaryText] = useState<string>("");
  const { permissions, currentUser, permissionLoading } = usePermissions();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const openSummary = () => {
    dispatch(setModalOpen({ id: MODAL_ID.SUMMARIZE_TIMELINE }));
  }

  const closeModalHandler = useCallback(() => {
    dispatch(setModalOpen({ id: null, data: null }));
  }, [dispatch]);

  useEffect(() => {
    if (outerActiveTab !== 0) {
      setActiveTab(2);
    } 
  }, [outerActiveTab]);

  if (permissionLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  if (insightsOnly) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#fff" }}>
        {!hideHeader && (
          <Typography
            fontSize={20}
            fontWeight={500}
            gutterBottom
            color="gray"
            sx={{ px: 2, pt: 1.5, pb: 0.5 }}
          >
            {leadName || "-"}
          </Typography>
        )}
        <Insights LeadId={leadId} fullHeight />
      </Box>
    );
  }

  return (
    <div className="p-2">
      {!hideHeader && (
        <Typography
          fontSize={20}
          fontWeight={500}
          gutterBottom
          color="gray"
          sx={{ ml: '20px' }}
        >
          {leadName || "-"}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/* LEFT SIDE (Tabs - takes remaining space) */}
        <Box sx={{ flex: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            sx={{
              pl: 2,
            }}
          >
           {outerActiveTab===0 && (<Tab label={`${type === "Customer" ? "Timeline" : "Opportunity Logs"}`} />)} 
    
        {outerActiveTab===0 && (<Tab label="Insights" />)}
            {type === "Customer" && (<Tab label="Details" />) }
          
        {isCustomer && currentUser?.CompanyDetail?.IsWhatsAppEnabled && outerActiveTab===0 && (
              <Tab label="WhatsApp" />
            )}
            
      </Tabs>
        </Box>

        {/* RIGHT SIDE (Auto width based on content) */}
        {type === "Customer" && activeTab === 2 && (
          <Box sx={{ pr: 2, whiteSpace: "nowrap" }}>
            <Button
              variant="contained"
              onClick={() => dispatch(setModalOpen({ id: MODAL_ID.EDITCUSTOMER, data: null }))}
            >
              Edit
            </Button>
          </Box>
        )}
        {type === "Customer" && activeTab === 0 && summaryText !== "" &&
          <Box
            sx={{
              pr: 2,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                },
              }}
              onClick={openSummary}
            >
              <Typography variant="h6">
                Summarize
              </Typography>
              <AutoAwesomeIcon />
            </Box>
          </Box>
        }
      </Box>

      <Box
        sx={{
          pt: 1
        }}
      >
        {/* Timeline Content */}
        {activeTab === 0 && <CustomerTimeline statusList={statusList} visitPurposeList={visitPurposeList} leadId={leadId} opportunityId={opportunityId} opportunityData={opportunityData} oppNumber={oppNumber} type={type} setSummaryText={setSummaryText} />}

        {/* Insights */}
        {activeTab === 1 && <Insights LeadId={leadId} />}

        {/* Details */}
        {activeTab === 2 && type === "Customer" && <Details key={`${leadId}-${outerActiveTab}`} LeadId={leadId} ActiveTab={outerActiveTab}/>}

        {isCustomer && currentUser?.CompanyDetail?.IsWhatsAppEnabled && activeTab === 3 && <WhatsAppDrawer LeadId={leadId} leadName={leadName} phoneNumber={phoneNumber} />}
      </Box>
      
      <AISummaryDialog
        open={modalOpen.id === MODAL_ID.SUMMARIZE_TIMELINE}
        onClose={closeModalHandler}
        text={summaryText}
      />

    </div>
  );
}

export default memo(TimelineDrawer);
